import React, { useState, useEffect, useRef } from "react";
import config from "../config";

const api = config.API_BASE_URL;

// ─── Auth helpers ─────────────────────────────────────────────
const getUser = () => {
  try {
    const raw = localStorage.getItem("admin");
    if (raw) {
      const p = JSON.parse(raw);
      return {
        username: p.username || "admin",
        fullNames: p.fullNames || p.username || "Admin",
        role: (p.role || "admin").toLowerCase(),
        profilePicture: p.profilePicture || null,
      };
    }
    const raw2 = localStorage.getItem("user");
    if (raw2) {
      const p = JSON.parse(raw2);
      return { username: p.username || "user", fullNames: p.fullNames || p.username || "User", role: "user", profilePicture: null };
    }
  } catch {}
  return { username: "admin", fullNames: "Admin", role: "admin", profilePicture: null };
};

// ─── Status config ────────────────────────────────────────────
const STATUS_CONFIG = {
  todo:       { label: "To Do",       color: "#579dff", bg: "rgba(87,157,255,0.12)",  emoji: "📋" },
  inprogress: { label: "In Progress", color: "#faa53d", bg: "rgba(250,165,61,0.12)",  emoji: "⚙️" },
  pending:    { label: "Pending",     color: "#f87168", bg: "rgba(248,113,104,0.12)", emoji: "⏳" },
  complete:   { label: "Complete",    color: "#4bce97", bg: "rgba(75,206,151,0.12)",  emoji: "✅" },
};

// ─── Animated Counter ─────────────────────────────────────────
function CountUp({ target, duration = 1200 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(timer); }
      else setVal(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return <span>{val.toLocaleString()}</span>;
}

// ─── Sparkline ────────────────────────────────────────────────
function Sparkline({ data = [], color = "#579dff", height = 40 }) {
  if (!data.length || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const w = 100, h = height;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * (h - 4) - 2}`).join(" ");
  const gradId = `sg${color.replace(/[^a-z0-9]/gi,"")}`;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${gradId})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ─── MiniBar ──────────────────────────────────────────────────
function MiniBar({ pct, color }) {
  return (
    <div style={{ width: "100%", height: 5, borderRadius: 99, background: "rgba(255,255,255,0.08)", overflow: "hidden", marginTop: 8 }}>
      <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 99, transition: "width 1.2s cubic-bezier(.4,0,.2,1)" }} />
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────
function Avatar({ name, size = 36, picBase64 = null }) {
  const COLORS = ["#579dff","#4bce97","#faa53d","#a78bfa","#f87168","#38bdf8","#fb923c","#facc15"];
  const color = COLORS[(name?.charCodeAt(0) || 0) % COLORS.length];
  if (picBase64) return (
    <img src={`data:image/jpeg;base64,${picBase64}`} alt={name}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.1)", flexShrink: 0 }} />
  );
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: `linear-gradient(135deg, ${color}, ${color}99)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: size * 0.4, flexShrink: 0, border: "2px solid rgba(255,255,255,0.08)" }}>
      {name?.[0]?.toUpperCase() ?? "?"}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ██████╗  ██████╗     ███████╗████████╗ █████╗ ████████╗███████╗
// ██╔══██╗██╔═══██╗    ██╔════╝╚══██╔══╝██╔══██╗╚══██╔══╝██╔════╝
// ██║  ██║██║   ██║    ███████╗   ██║   ███████║   ██║   █████╗
// ██║  ██║██║   ██║    ╚════██║   ██║   ██╔══██║   ██║   ██╔══╝
// ██████╔╝╚██████╔╝    ███████║   ██║   ██║  ██║   ██║   ███████╗
// ╚═════╝  ╚═════╝     ╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝   ╚══════╝
//  4D ANIMATED ICONS — SVG with CSS animations
// ═══════════════════════════════════════════════════════════════

// Lists / Boards — rotating 3D cube with list lines
function Icon4D_Lists() {
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" style={{ overflow: "visible" }}>
      <style>{`
        @keyframes cubeRot { 0%{transform:rotateY(0deg) rotateX(10deg)} 100%{transform:rotateY(360deg) rotateX(10deg)} }
        @keyframes linePulse1 { 0%,100%{transform:scaleX(1)} 50%{transform:scaleX(0.55)} }
        @keyframes linePulse2 { 0%,100%{transform:scaleX(0.8)} 50%{transform:scaleX(1)} }
        @keyframes linePulse3 { 0%,100%{transform:scaleX(0.65)} 50%{transform:scaleX(0.9)} }
        @keyframes glowDot { 0%,100%{opacity:0.5;r:1.5} 50%{opacity:1;r:2.2} }
        .cube-wrap { animation: cubeRot 4s linear infinite; transform-origin:19px 19px; transform-box:fill-box; }
        .lp1 { animation: linePulse1 2s ease-in-out infinite; transform-origin: left center; transform-box: fill-box; }
        .lp2 { animation: linePulse2 2s ease-in-out infinite 0.3s; transform-origin: left center; transform-box: fill-box; }
        .lp3 { animation: linePulse3 2s ease-in-out infinite 0.6s; transform-origin: left center; transform-box: fill-box; }
      `}</style>
      <g className="cube-wrap">
        {/* 3D cube faces */}
        <polygon points="19,3 32,10.5 19,18 6,10.5" fill="#579dff" opacity="0.95"/>
        <polygon points="6,10.5 19,18 19,32 6,24.5" fill="#3178cc" opacity="0.8"/>
        <polygon points="19,18 32,10.5 32,24.5 19,32" fill="#1a58a8" opacity="0.65"/>
        {/* Shimmer on top face */}
        <polygon points="19,3 32,10.5 19,18 6,10.5" fill="url(#cubeglow)" opacity="0.3"/>
        {/* List lines on top face */}
        <g className="lp1"><line x1="11" y1="9.5" x2="27" y2="9.5" stroke="rgba(255,255,255,0.85)" strokeWidth="1.4" strokeLinecap="round"/></g>
        <g className="lp2"><line x1="12" y1="12.5" x2="26" y2="12.5" stroke="rgba(255,255,255,0.6)" strokeWidth="1.1" strokeLinecap="round"/></g>
        <g className="lp3"><line x1="13.5" y1="15" x2="24.5" y2="15" stroke="rgba(255,255,255,0.4)" strokeWidth="0.9" strokeLinecap="round"/></g>
        {/* Top glow dot */}
        <circle cx="19" cy="3" fill="#90c8ff">
          <animate attributeName="r" values="1.5;2.2;1.5" dur="1.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite"/>
        </circle>
      </g>
      <defs>
        <radialGradient id="cubeglow" cx="40%" cy="30%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.9"/>
          <stop offset="100%" stopColor="#fff" stopOpacity="0"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

// Completed — orbiting sphere with checkmark
function Icon4D_Check() {
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" style={{ overflow: "visible" }}>
      <style>{`
        @keyframes orbitRing1 { to { transform: rotate(360deg); } }
        @keyframes orbitRing2 { to { transform: rotate(-360deg); } }
        @keyframes spherePop { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
        @keyframes orbitDot { }
        .ck-r1 { animation: orbitRing1 3s linear infinite; transform-origin:19px 18px; transform-box:fill-box; }
        .ck-r2 { animation: orbitRing2 4.5s linear infinite; transform-origin:19px 18px; transform-box:fill-box; }
        .ck-sphere { animation: spherePop 2s ease-in-out infinite; transform-origin:19px 18px; transform-box:fill-box; }
      `}</style>
      {/* Shadow */}
      <ellipse cx="19" cy="31" rx="10" ry="2.5" fill="#0a2a1a" opacity="0.5"/>
      {/* Orbit rings */}
      <g className="ck-r1">
        <ellipse cx="19" cy="18" rx="16" ry="5" stroke="#4bce97" strokeWidth="0.8" strokeDasharray="5 4" fill="none" opacity="0.5"/>
      </g>
      <g className="ck-r2">
        <ellipse cx="19" cy="18" rx="5" ry="15" stroke="#2ea87a" strokeWidth="0.7" strokeDasharray="4 5" fill="none" opacity="0.35"/>
      </g>
      {/* 3D sphere */}
      <g className="ck-sphere">
        <circle cx="19" cy="18" r="11" fill="#0e2a1c"/>
        <circle cx="19" cy="18" r="11" fill="url(#ckGrad)"/>
        {/* Highlight */}
        <ellipse cx="15" cy="14" rx="4" ry="2.5" fill="rgba(255,255,255,0.18)" transform="rotate(-30 15 14)"/>
        {/* Checkmark */}
        <polyline points="12,18 17,23 26,12" stroke="#fff" strokeWidth="2.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
      {/* Orbiting particle */}
      <circle r="2.2" fill="#4bce97" opacity="0.9">
        <animateMotion dur="3s" repeatCount="indefinite" path="M 19 2 A 16 5 0 1 1 18.9 2"/>
        <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite"/>
      </circle>
      <defs>
        <radialGradient id="ckGrad" cx="35%" cy="28%">
          <stop offset="0%" stopColor="#7effc8"/>
          <stop offset="55%" stopColor="#4bce97"/>
          <stop offset="100%" stopColor="#1a6644"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

// In Progress — interlocking gears with sparks
function Icon4D_Gear() {
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" style={{ overflow: "visible" }}>
      <style>{`
        @keyframes bigGear { to { transform: rotate(360deg); } }
        @keyframes smallGear { to { transform: rotate(-360deg); } }
        @keyframes sparkFloat { 0%{opacity:0;transform:translate(0,0) scale(0)} 40%{opacity:1} 100%{opacity:0;transform:translate(var(--tx),var(--ty)) scale(0)} }
        .bg-gear { animation: bigGear 3s linear infinite; transform-origin:19px 18px; transform-box:fill-box; }
        .sm-gear { animation: smallGear 1.8s linear infinite; transform-origin:10px 27px; transform-box:fill-box; }
        .spark1 { --tx:-5px; --ty:-8px; animation: sparkFloat 1.5s ease-out infinite 0s; transform-origin:22px 14px; transform-box:fill-box; }
        .spark2 { --tx:6px; --ty:-6px; animation: sparkFloat 1.5s ease-out infinite 0.5s; transform-origin:26px 16px; transform-box:fill-box; }
        .spark3 { --tx:3px; --ty:-9px; animation: sparkFloat 1.5s ease-out infinite 1s; transform-origin:24px 12px; transform-box:fill-box; }
      `}</style>
      {/* Shadow */}
      <ellipse cx="17" cy="32" rx="11" ry="2.5" fill="#200a00" opacity="0.4"/>
      {/* Big gear */}
      <g className="bg-gear">
        <path d="M19 7 L20.8 4 L19 2 L17.2 4 L19 7Z M25.5 8.5 L28 6.5 L28.5 4 L26 5.5 L25.5 8.5Z M30 16 L33 14.5 L33 12.5 L30 14 L30 16Z M29 23 L32 24.5 L33 22 L30 21 L29 23Z M23.5 28.5 L24 32 L26.5 31.5 L26 28 L23.5 28.5Z M14.5 28.5 L14 32 L11.5 31.5 L12 28 L14.5 28.5Z M9 23 L6 24.5 L5 22 L8 21 L9 23Z M8 16 L5 14.5 L5 12.5 L8 14 L8 16Z M12.5 8.5 L10 6.5 L9.5 4 L12 5.5 L12.5 8.5Z" fill="#d06000" opacity="0.85"/>
        <circle cx="19" cy="18" r="10" fill="#a04800"/>
        <circle cx="19" cy="18" r="10" fill="url(#gearGrad)"/>
        <circle cx="19" cy="18" r="5.5" fill="rgba(0,0,0,0.5)"/>
        <circle cx="19" cy="18" r="3.5" fill="url(#gearCenter)"/>
        {/* Highlight */}
        <ellipse cx="15.5" cy="14.5" rx="3.5" ry="2" fill="rgba(255,255,255,0.15)" transform="rotate(-25 15.5 14.5)"/>
      </g>
      {/* Small gear */}
      <g className="sm-gear">
        <path d="M10 22.5 L11.2 20.8 L10 19 L8.8 20.8 L10 22.5Z M13 22.2 L14.5 21.5 L14.5 20 L13 20.7 L13 22.2Z M13.2 25 L14.7 25.5 L14.7 24 L13.2 24 L13.2 25Z M10 27 L11.2 28.7 L8.8 28.7 L8.8 27 L10 27Z M7 25 L5.5 25.5 L5.5 24 L7 24 L7 25Z M6.8 22.2 L5.3 21.5 L5.3 20 L6.8 20.7 L6.8 22.2Z" fill="#faa53d" opacity="0.75"/>
        <circle cx="10" cy="24" r="4.5" fill="#7a3200" opacity="0.9"/>
        <circle cx="10" cy="24" r="2.5" fill="#faa53d" opacity="0.9"/>
      </g>
      {/* Sparks */}
      <circle className="spark1" cx="22" cy="14" r="1.2" fill="#ffcc44"/>
      <circle className="spark2" cx="26" cy="16" r="1" fill="#ff9922"/>
      <circle className="spark3" cx="24" cy="12" r="0.9" fill="#ffee66"/>
      {/* Glow ring */}
      <circle cx="19" cy="18" r="10" stroke="#ff9a2e" strokeWidth="1" fill="none" opacity="0.25">
        <animate attributeName="opacity" values="0.1;0.4;0.1" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="r" values="10;12;10" dur="2s" repeatCount="indefinite"/>
      </circle>
      <defs>
        <radialGradient id="gearGrad" cx="32%" cy="26%">
          <stop offset="0%" stopColor="#ffb84a"/>
          <stop offset="100%" stopColor="#d06000"/>
        </radialGradient>
        <radialGradient id="gearCenter" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#ffd080"/>
          <stop offset="100%" stopColor="#faa53d"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

// Messages — floating 3D chat bubbles with typing dots
function Icon4D_Message() {
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" style={{ overflow: "visible" }}>
      <style>{`
        @keyframes bubbleFloat { 0%,100%{transform:translateY(0) rotate(-4deg)} 50%{transform:translateY(-4px) rotate(-4deg)} }
        @keyframes bubble2Float { 0%,100%{transform:translateY(0) rotate(2deg)} 50%{transform:translateY(-2.5px) rotate(2deg)} }
        @keyframes typingDot { 0%,80%,100%{transform:scaleY(0.5);opacity:0.35} 40%{transform:scaleY(1);opacity:1} }
        @keyframes badgePulse { 0%,100%{r:4} 50%{r:5} }
        @keyframes msgRing { to{transform:rotate(360deg)} }
        .mb1 { animation: bubbleFloat 2.8s ease-in-out infinite; transform-origin:16px 18px; transform-box:fill-box; }
        .mb2 { animation: bubble2Float 2.8s ease-in-out infinite 0.4s; transform-origin:22px 22px; transform-box:fill-box; }
        .td1 { animation: typingDot 1.3s ease-in-out infinite 0s; transform-origin:12px 17px; transform-box:fill-box; }
        .td2 { animation: typingDot 1.3s ease-in-out infinite 0.22s; transform-origin:16px 17px; transform-box:fill-box; }
        .td3 { animation: typingDot 1.3s ease-in-out infinite 0.44s; transform-origin:20px 17px; transform-box:fill-box; }
        .msg-ring { animation: msgRing 5s linear infinite; transform-origin:19px 19px; transform-box:fill-box; }
      `}</style>
      {/* Outer orbit */}
      <circle className="msg-ring" cx="19" cy="19" r="17" stroke="#a78bfa" strokeWidth="0.5" strokeDasharray="3 7" fill="none" opacity="0.25"/>
      {/* Shadow */}
      <ellipse cx="19" cy="33" rx="9" ry="2" fill="#100820" opacity="0.4"/>
      {/* Back bubble */}
      <g className="mb2">
        <rect x="15" y="16" width="17" height="11" rx="5.5" fill="#3a1878" opacity="0.65"/>
        <polygon points="20,27 18,32 25,27" fill="#2e1260"/>
      </g>
      {/* Main bubble */}
      <g className="mb1">
        <rect x="5" y="10" width="21" height="14" rx="7" fill="url(#msgGrad)"/>
        <polygon points="9,24 7,30 15,24" fill="#5a28b8"/>
        {/* Shine */}
        <ellipse cx="12" cy="14" rx="5" ry="2.5" fill="rgba(255,255,255,0.12)" transform="rotate(-10 12 14)"/>
        {/* Typing dots */}
        <circle className="td1" cx="12" cy="17" r="1.8" fill="rgba(255,255,255,0.9)"/>
        <circle className="td2" cx="16" cy="17" r="1.8" fill="rgba(255,255,255,0.9)"/>
        <circle className="td3" cx="20" cy="17" r="1.8" fill="rgba(255,255,255,0.9)"/>
      </g>
      {/* Unread badge */}
      <g>
        <circle cx="31" cy="9" fill="#f87168">
          <animate attributeName="r" values="4;5.2;4" dur="1.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="31" cy="9" r="3" fill="none" stroke="#f87168" strokeWidth="1.5" opacity="0.4">
          <animate attributeName="r" values="4.5;7;4.5" dur="1.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.5;0;0.5" dur="1.5s" repeatCount="indefinite"/>
        </circle>
        <text x="31" y="12" textAnchor="middle" fontSize="5.5" fill="white" fontWeight="bold">!</text>
      </g>
      <defs>
        <linearGradient id="msgGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#9b6dff"/>
          <stop offset="100%" stopColor="#6340c8"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

// Team — three people with crown + halo
function Icon4D_Team() {
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" style={{ overflow: "visible" }}>
      <style>{`
        @keyframes personBob1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
        @keyframes personBob2 { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-5px) scale(1.04)} }
        @keyframes personBob3 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
        @keyframes haloSpin { to{transform:rotate(360deg)} }
        @keyframes crownShine { 0%,100%{opacity:0.9} 50%{opacity:1;filter:brightness(1.4)} }
        .tp1 { animation: personBob1 2.4s ease-in-out infinite; transform-origin:11px 16px; transform-box:fill-box; }
        .tp2 { animation: personBob2 2.4s ease-in-out infinite 0.3s; transform-origin:19px 14px; transform-box:fill-box; }
        .tp3 { animation: personBob3 2.4s ease-in-out infinite 0.6s; transform-origin:27px 16px; transform-box:fill-box; }
        .halo { animation: haloSpin 4s linear infinite; transform-origin:19px 9px; transform-box:fill-box; }
        .crown { animation: crownShine 2s ease-in-out infinite; }
      `}</style>
      {/* Shadow */}
      <ellipse cx="19" cy="33" rx="13" ry="2.2" fill="#200000" opacity="0.35"/>
      {/* Person left */}
      <g className="tp1">
        <circle cx="11" cy="11" r="4.5" fill="url(#teamG1)"/>
        <ellipse cx="11" cy="11" rx="3" ry="1.5" fill="rgba(255,255,255,0.12)" transform="rotate(-20 11 9)"/>
        <path d="M3 26 Q3 19 11 19 Q19 19 19 26" fill="#c23030" opacity="0.7"/>
      </g>
      {/* Person right */}
      <g className="tp3">
        <circle cx="27" cy="11" r="4.5" fill="url(#teamG3)"/>
        <ellipse cx="27" cy="11" rx="3" ry="1.5" fill="rgba(255,255,255,0.12)" transform="rotate(20 27 9)"/>
        <path d="M19 26 Q19 19 27 19 Q35 19 35 26" fill="#c23030" opacity="0.7"/>
      </g>
      {/* Center hero */}
      <g className="tp2">
        {/* Halo */}
        <g className="halo">
          <ellipse cx="19" cy="5" rx="7" ry="2" stroke="#facc15" strokeWidth="1.2" fill="none" opacity="0.7"/>
        </g>
        <circle cx="19" cy="9" r="6" fill="url(#teamG2)"/>
        <ellipse cx="16" cy="7" rx="2.5" ry="1.5" fill="rgba(255,255,255,0.15)" transform="rotate(-20 16 7)"/>
        <path d="M9 28 Q9 20 19 20 Q29 20 29 28" fill="#f87168" opacity="0.9"/>
        {/* Crown */}
        <g className="crown">
          <path d="M15 3.5 L16.5 6.5 L19 4.5 L21.5 6.5 L23 3.5 L23 3 L15 3Z" fill="#facc15"/>
          <circle cx="15" cy="3.5" r="1" fill="#fbbf24"/>
          <circle cx="19" cy="4" r="1.1" fill="#fbbf24"/>
          <circle cx="23" cy="3.5" r="1" fill="#fbbf24"/>
        </g>
      </g>
      <defs>
        <radialGradient id="teamG1" cx="35%" cy="28%"><stop offset="0%" stopColor="#ff8888"/><stop offset="100%" stopColor="#c23030"/></radialGradient>
        <radialGradient id="teamG2" cx="35%" cy="28%"><stop offset="0%" stopColor="#ffaaaa"/><stop offset="100%" stopColor="#f87168"/></radialGradient>
        <radialGradient id="teamG3" cx="35%" cy="28%"><stop offset="0%" stopColor="#ff8888"/><stop offset="100%" stopColor="#c23030"/></radialGradient>
      </defs>
    </svg>
  );
}

// Task Board — tilting kanban board with animated cards
function Icon4D_Task() {
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" style={{ overflow: "visible" }}>
      <style>{`
        @keyframes boardTilt { 0%,100%{transform:perspective(180px) rotateX(6deg) rotateY(-6deg)} 50%{transform:perspective(180px) rotateX(-4deg) rotateY(5deg)} }
        @keyframes cardSlide1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2.5px)} }
        @keyframes cardSlide2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3.5px)} }
        @keyframes cardSlide3 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
        .brd { animation: boardTilt 3.5s ease-in-out infinite; transform-origin:19px 20px; transform-box:fill-box; }
        .bc1 { animation: cardSlide1 2s ease-in-out infinite 0s; transform-origin:9px 15px; transform-box:fill-box; }
        .bc2 { animation: cardSlide2 2s ease-in-out infinite 0.35s; transform-origin:19px 18px; transform-box:fill-box; }
        .bc3 { animation: cardSlide3 2s ease-in-out infinite 0.7s; transform-origin:29px 14px; transform-box:fill-box; }
      `}</style>
      <g className="brd">
        {/* Board bg */}
        <rect x="3" y="6" width="32" height="28" rx="4" fill="#0b1a30" stroke="#1a3a5c" strokeWidth="0.8"/>
        {/* Column separators */}
        <line x1="14" y1="10" x2="14" y2="32" stroke="rgba(87,157,255,0.18)" strokeWidth="0.8"/>
        <line x1="24" y1="10" x2="24" y2="32" stroke="rgba(87,157,255,0.18)" strokeWidth="0.8"/>
        {/* Column headers */}
        <rect x="5" y="7.5" width="7.5" height="2.5" rx="1.5" fill="#579dff" opacity="0.65"/>
        <rect x="15.5" y="7.5" width="7" height="2.5" rx="1.5" fill="#faa53d" opacity="0.65"/>
        <rect x="25.5" y="7.5" width="7" height="2.5" rx="1.5" fill="#4bce97" opacity="0.65"/>
        {/* Col 1 cards */}
        <g className="bc1">
          <rect x="5" y="12" width="7.5" height="7" rx="1.5" fill="#1a3a5c"/>
          <rect x="6" y="13.5" width="5.5" height="1.1" rx="0.5" fill="#579dff" opacity="0.75"/>
          <rect x="6" y="15.5" width="4" height="1" rx="0.5" fill="rgba(255,255,255,0.2)"/>
          <rect x="6" y="17" width="4.5" height="1" rx="0.5" fill="rgba(255,255,255,0.15)"/>
        </g>
        <rect x="5" y="21" width="7.5" height="5" rx="1.5" fill="#162840" opacity="0.7"/>
        <rect x="6" y="22.5" width="5.5" height="1" rx="0.5" fill="#579dff" opacity="0.45"/>
        {/* Col 2 cards */}
        <g className="bc2">
          <rect x="15.5" y="12" width="7" height="8.5" rx="1.5" fill="#1a1400" opacity="0.9"/>
          <rect x="15.5" y="12" width="7" height="8.5" rx="1.5" fill="rgba(250,165,61,0.12)"/>
          <rect x="16.5" y="13.5" width="5" height="1.1" rx="0.5" fill="#faa53d" opacity="0.85"/>
          <rect x="16.5" y="15.5" width="3.5" height="1" rx="0.5" fill="rgba(255,255,255,0.2)"/>
          <rect x="16.5" y="17" width="4" height="1" rx="0.5" fill="rgba(255,255,255,0.15)"/>
          <rect x="16.5" y="18.5" width="3" height="1" rx="0.5" fill="rgba(255,255,255,0.1)"/>
        </g>
        {/* Col 3 cards */}
        <g className="bc3">
          <rect x="25.5" y="12" width="7" height="6" rx="1.5" fill="rgba(75,206,151,0.1)"/>
          <rect x="26.5" y="13.5" width="5" height="1.1" rx="0.5" fill="#4bce97" opacity="0.75"/>
          <polyline points="26.5,17 28,18.5 31,15.5" stroke="#4bce97" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        {/* Shine overlay on board */}
        <rect x="3" y="6" width="32" height="8" rx="4" fill="url(#boardShine)" opacity="0.25"/>
      </g>
      <defs>
        <linearGradient id="boardShine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.4"/>
          <stop offset="100%" stopColor="#fff" stopOpacity="0"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

// ─── StatCard ─────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, gradient, sparkData, sparkColor, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div style={{
      background: gradient,
      borderRadius: 18,
      padding: "20px 22px 14px",
      border: "1px solid rgba(255,255,255,0.07)",
      boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(24px)",
      transition: "opacity 0.5s ease, transform 0.5s ease",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", margin: 0 }}>{label}</p>
          <h3 style={{ fontSize: 40, fontWeight: 900, color: "#fff", margin: "5px 0 2px", lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>
            {visible ? <CountUp target={typeof value === "number" ? value : 0} /> : "0"}
          </h3>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, margin: 0 }}>{sub}</p>
        </div>
        {/* 4D Icon container */}
        <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 16, padding: "8px", backdropFilter: "blur(4px)", flexShrink: 0 }}>
          {icon}
        </div>
      </div>
      {sparkData?.length > 1 && (
        <div style={{ marginTop: 14 }}>
          <Sparkline data={sparkData} color={sparkColor || "#fff"} height={36} />
        </div>
      )}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────
function SectionHeader({ title, sub, action, onAction }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 }}>
      <div>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: "#e2e8f0", margin: 0 }}>{title}</h3>
        {sub && <p style={{ fontSize: 11, color: "#4a6a8a", margin: "2px 0 0" }}>{sub}</p>}
      </div>
      {action && (
        <button onClick={onAction} style={{ fontSize: 11, color: "#579dff", background: "rgba(87,157,255,0.1)", border: "1px solid rgba(87,157,255,0.2)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
          {action} →
        </button>
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────
function Panel({ children, style = {} }) {
  return (
    <div style={{
      background: "rgba(8,14,26,0.88)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 18,
      padding: 22,
      backdropFilter: "blur(14px)",
      boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
      ...style,
    }}>
      {children}
    </div>
  );
}

// ─── Pill ─────────────────────────────────────────────────────
function Pill({ label, color, bg }) {
  return (
    <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, color, background: bg, border: `1px solid ${color}44`, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

// ─── Loader ───────────────────────────────────────────────────
function Loader() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", border: "3px solid rgba(87,157,255,0.15)", borderTop: "3px solid #579dff", animation: "dspin 0.8s linear infinite" }} />
      <span style={{ color: "#3a5a7a", fontSize: 13 }}>Loading dashboard…</span>
    </div>
  );
}

// ─── Empty ────────────────────────────────────────────────────
function Empty({ icon = "📭", msg = "No data yet" }) {
  return (
    <div style={{ textAlign: "center", padding: "28px 20px", color: "#2d4a6a" }}>
      <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>{icon}</div>
      <p style={{ fontSize: 12, margin: 0 }}>{msg}</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ══════════════════════════════════════════════════════════════
export default function Dashboard() {
  const user = useRef(getUser()).current;
  const isSuperAdmin = user.role === "superadmin";

  const [boards, setBoards]     = useState([]);
  const [cards, setCards]       = useState([]);
  const [messages, setMessages] = useState([]);
  const [admins, setAdmins]     = useState([]);
  const [convos, setConvos]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [now, setNow]           = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ── Fetch all data ─────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const fetches = [
          fetch(`${api}/boards`).then(r => r.ok ? r.json() : []),
          fetch(`${api}/cards`).then(r => r.ok ? r.json() : []),
          fetch(`${api}/Messages/admin/${user.username}`).then(r => r.ok ? r.json() : []),
        ];
        if (isSuperAdmin) fetches.push(fetch(`${api}/Admin`).then(r => r.ok ? r.json() : []));

        const results = await Promise.allSettled(fetches);
        const [bRes, cRes, mRes, aRes] = results.map(r => r.status === "fulfilled" ? r.value : []);

        const allBoards = bRes || [];

        // Superadmin sees all boards; admin sees own
        const myBoards = isSuperAdmin
          ? allBoards
          : allBoards.filter(b => b.assignedTo === user.username || b.createdBy === user.username);
        setBoards(myBoards);

        // Parse all cards (we keep all, filter in derived)
        const parsed = (cRes || []).map(c => ({
          ...c,
          status: c.status ?? "todo",
          description: c.description ?? "",
          checklist: (() => { try { return Array.isArray(c.checklist) ? c.checklist : JSON.parse(c.checklist || "[]"); } catch { return []; } })(),
        }));
        setCards(parsed);

        setMessages(mRes || []);
        if (isSuperAdmin && aRes) setAdmins(aRes);

        // Build convos map
        const userSet = {};
        (mRes || []).forEach(m => {
          if (!userSet[m.user]) userSet[m.user] = { count: 0, last: null };
          userSet[m.user].count++;
          if (!userSet[m.user].last || new Date(m.timestamp) > new Date(userSet[m.user].last))
            userSet[m.user].last = m.timestamp;
        });
        setConvos(Object.entries(userSet).map(([u, d]) => ({ username: u, ...d })));
      } catch (e) { console.error("Dashboard fetch error:", e); }
      finally { setLoading(false); }
    })();
  }, [isSuperAdmin, user.username]);

  // ── Derived data ───────────────────────────────────────────
  const myBoardIds = new Set(boards.map(b => b.id));

  // Active (non-archived) cards on MY boards
  const activeCards = cards.filter(c => !c.archived && myBoardIds.has(c.colId));

  // ═══ ASSIGNED CARDS — cards that belong to current user ════
  // A card is "mine" if:
  //   1. c.createdBy === user.username, OR
  //   2. The board it belongs to has assignedTo === user.username
  // This section NEVER disappears — always rendered
  const assignedCards = cards.filter(c => {
    if (c.archived) return false;
    if (c.createdBy === user.username) return true;
    const board = boards.find(b => b.id === c.colId);
    return board && (board.assignedTo === user.username || board.createdBy === user.username);
  });

  // Sort: incomplete first (by most recent), then complete
  const assignedNotDone = assignedCards.filter(c => c.status !== "complete").sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const assignedDone    = assignedCards.filter(c => c.status === "complete").sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const sortedAssigned  = [...assignedNotDone, ...assignedDone].slice(0, 12);

  // Cards to count in stats (superadmin = all active; admin = only assigned)
  const displayCards = isSuperAdmin ? activeCards : assignedCards;

  const byStatus = (s) => displayCards.filter(c => c.status === s).length;
  const totalBoards    = boards.length;
  const totalCards     = displayCards.length;
  const completedCards = byStatus("complete");
  const inProgCards    = byStatus("inprogress");
  const pendingCards   = byStatus("pending");
  const todoCards      = byStatus("todo");
  const completionRate = totalCards > 0 ? Math.round((completedCards / totalCards) * 100) : 0;
  const unreadMessages = messages.filter(m => !m.read && m.senderType !== "admin").length;

  // Sparklines
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    return d.toDateString();
  });
  const cardsByDay = last7.map(day => displayCards.filter(c => new Date(c.createdAt).toDateString() === day).length);

  // Recent cards (latest first)
  const recentCards = [...displayCards].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8);

  // Assignee map (for admin activity)
  const assigneeMap = {};
  boards.forEach(b => { const a = b.assignedTo || b.createdBy; if (a) assigneeMap[a] = (assigneeMap[a] || 0) + 1; });
  const topAssignees = Object.entries(assigneeMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Board progress
  const boardProgress = boards.slice(0, 6).map(b => {
    const bCards = displayCards.filter(c => c.colId === b.id);
    const done = bCards.filter(c => c.status === "complete").length;
    return { ...b, total: bCards.length, done, pct: bCards.length > 0 ? Math.round((done / bCards.length) * 100) : 0 };
  });

  const recentMessages = [...messages].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 5);

  const fmtTime = (ts) => {
    if (!ts) return "";
    const diff = Math.floor((new Date() - new Date(ts)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(ts).toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const greeting = () => {
    const h = now.getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  };

  if (loading) return (
    <>
      <style>{`@keyframes dspin { to { transform: rotate(360deg); } }`}</style>
      <Loader />
    </>
  );

  return (
    <>
      <style>{`
        @keyframes dspin    { to { transform: rotate(360deg); } }
        @keyframes dfadein  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes dpulse   { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes dshimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .drow:hover  { background: rgba(87,157,255,0.05) !important; }
        .dmrow:hover { background: rgba(255,255,255,0.03) !important; }
        .darow:hover { background: rgba(87,157,255,0.08) !important; }
      `}</style>

      <div style={{ display: "flex", flexDirection: "column", gap: 24, animation: "dfadein 0.4s ease" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <p style={{ color: "#2d5a7a", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 4px" }}>
              {greeting()}, {user.fullNames.split(" ")[0]}
            </p>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: "#e2e8f0", margin: 0, letterSpacing: "-0.02em" }}>
              Dashboard Overview
            </h1>
            <p style={{ color: "#2d4a6a", fontSize: 12, margin: "5px 0 0", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4bce97", display: "inline-block", animation: "dpulse 2s infinite" }} />
              {isSuperAdmin
                ? `All data visible · ${admins.length} admins · ${now.toLocaleTimeString()}`
                : `Your workspace · ${assignedNotDone.length} active tasks · ${now.toLocaleTimeString()}`
              }
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name={user.fullNames} size={46} picBase64={user.profilePicture} />
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{user.fullNames}</p>
              <p style={{ margin: 0, fontSize: 10, color: isSuperAdmin ? "#f59e0b" : "#579dff", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {isSuperAdmin ? "⭐ Superadmin" : `👤 ${user.role}`}
              </p>
            </div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14 }}>
          <StatCard label="Total Lists"   value={totalBoards}     sub={`${totalCards} cards total`}                                icon={<Icon4D_Lists />}   gradient="linear-gradient(135deg,#0c1e38 0%,#060f1e 100%)" sparkData={cardsByDay}                             sparkColor="#579dff" delay={0}   />
          <StatCard label="Completed"     value={completedCards}  sub={`${completionRate}% completion rate`}                       icon={<Icon4D_Check />}   gradient="linear-gradient(135deg,#071e14 0%,#030f0a 100%)" sparkData={last7.map((_,i)=>i*4)}                  sparkColor="#4bce97" delay={80}  />
          <StatCard label="In Progress"   value={inProgCards}     sub={`${pendingCards} pending · ${todoCards} todo`}              icon={<Icon4D_Gear />}    gradient="linear-gradient(135deg,#1c0e00 0%,#0d0700 100%)" sparkData={last7.map((_,i)=>Math.abs(Math.sin(i)*9))} sparkColor="#faa53d" delay={160} />
          <StatCard label="Messages"      value={messages.length} sub={`${unreadMessages > 0 ? `${unreadMessages} unread` : "All read"} · ${convos.length} convos`} icon={<Icon4D_Message />} gradient="linear-gradient(135deg,#0e0618 0%,#07030e 100%)" sparkData={last7.map((_,i)=>i+1)}                 sparkColor="#a78bfa" delay={240} />
          {isSuperAdmin && (
            <StatCard label="Team Members" value={admins.length} sub="Active admins" icon={<Icon4D_Team />} gradient="linear-gradient(135deg,#180606 0%,#0b0303 100%)" sparkData={[1,1,2,2,2,admins.length-1,admins.length]} sparkColor="#f87168" delay={320} />
          )}
        </div>

        {/* ── Status Distribution ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
          {Object.entries(STATUS_CONFIG).map(([key, s]) => {
            const cnt = byStatus(key);
            const pct = totalCards > 0 ? Math.round((cnt / totalCards) * 100) : 0;
            return (
              <Panel key={key} style={{ padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ fontSize: 14 }}>{s.emoji}</span>
                    <span style={{ fontSize: 11, color: "#5a7a9a", fontWeight: 600 }}>{s.label}</span>
                  </div>
                  <span style={{ fontSize: 22, fontWeight: 900, color: s.color, fontFamily: "'DM Mono', monospace" }}>{cnt}</span>
                </div>
                <MiniBar pct={pct} color={s.color} />
                <p style={{ fontSize: 10, color: "#2d4a6a", margin: "4px 0 0", textAlign: "right" }}>{pct}%</p>
              </Panel>
            );
          })}
        </div>

        {/* ══════════════════════════════════════════════════════
            ██████╗ ██╗███╗   ██╗███╗   ██╗███████╗██████╗
            ██╔══██╗██║████╗  ██║████╗  ██║██╔════╝██╔══██╗
            ██████╔╝██║██╔██╗ ██║██╔██╗ ██║█████╗  ██║  ██║
            ██╔═══╝ ██║██║╚██╗██║██║╚██╗██║██╔══╝  ██║  ██║
            ██║     ██║██║ ╚████║██║ ╚████║███████╗██████╔╝
            CARDS — assigned to current user, ALWAYS visible
        ══════════════════════════════════════════════════════ */}
        <Panel style={{ borderLeft: "3px solid #579dff", borderRadius: "0 18px 18px 18px" }}>
          <SectionHeader
            title={isSuperAdmin ? "📋 All Board Cards" : "📌 My Assigned Cards"}
            sub={
              isSuperAdmin
                ? `${sortedAssigned.length} shown · ${assignedNotDone.length} active`
                : `${sortedAssigned.length} assigned to you · ${assignedNotDone.length} still active`
            }
          />
          {sortedAssigned.length === 0 ? (
            <Empty icon="📌" msg={isSuperAdmin ? "No cards found" : "No cards assigned to you yet"} />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
              {sortedAssigned.map(card => {
                const st   = STATUS_CONFIG[card.status] || STATUS_CONFIG.todo;
                const board = boards.find(b => b.id === card.colId);
                const cl    = card.checklist || [];
                const done  = cl.filter(i => i.done).length;
                const pct   = cl.length > 0 ? Math.round((done / cl.length) * 100) : null;
                return (
                  <div key={card.id} className="darow" style={{
                    padding: "11px 14px 11px 16px",
                    borderRadius: 12,
                    border: `1px solid ${st.color}20`,
                    background: card.status === "complete" ? "rgba(75,206,151,0.04)" : `${st.color}07`,
                    transition: "background 0.15s",
                    cursor: "default",
                    position: "relative",
                    overflow: "hidden",
                    opacity: card.status === "complete" ? 0.65 : 1,
                  }}>
                    {/* Left accent */}
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: st.color, borderRadius: "3px 0 0 3px" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 5 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: card.status === "complete" ? "#6b8caf" : "#dce8f5", margin: 0, lineHeight: 1.4, flex: 1, textDecoration: card.status === "complete" ? "line-through" : "none" }}>
                        {card.title}
                      </p>
                      <Pill label={st.label} color={st.color} bg={st.bg} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {board && (
                        <span style={{ fontSize: 10, color: "#3a5a7a", display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: board.color || "#579dff", display: "inline-block" }} />
                          {board.title}
                        </span>
                      )}
                      {cl.length > 0 && (
                        <span style={{ fontSize: 10, color: pct === 100 ? "#4bce97" : "#3a5a7a" }}>
                          ☑ {done}/{cl.length}
                        </span>
                      )}
                      {card.createdBy && <span style={{ fontSize: 10, color: "#2d4a6a" }}>👤 {card.createdBy}</span>}
                      {card.createdAt && <span style={{ fontSize: 10, color: "#1e3a5a", marginLeft: "auto" }}>{fmtTime(card.createdAt)}</span>}
                    </div>
                    {cl.length > 0 && (
                      <div style={{ marginTop: 8, width: "100%", height: 3, borderRadius: 99, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#4bce97" : st.color, borderRadius: 99, transition: "width 1s ease", boxShadow: pct === 100 ? "0 0 6px #4bce9766" : "none" }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        {/* ── Main Grid ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20 }}>

          {/* Left */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Board Progress */}
            <Panel>
              <SectionHeader title="Board Progress" sub={`${boards.length} list${boards.length !== 1 ? "s" : ""}`} />
              {boardProgress.length === 0 ? <Empty icon="📋" msg="No boards yet" /> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {boardProgress.map(b => (
                    <div key={b.id}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 9, height: 9, borderRadius: "50%", background: b.color || "#579dff", flexShrink: 0, boxShadow: `0 0 5px ${b.color||"#579dff"}66` }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#dce8f5", maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title}</span>
                          {b.assignedTo && <span style={{ fontSize: 10, color: "#2d4a6a" }}>👤 {b.assignedTo}</span>}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 11, color: "#2d4a6a" }}>{b.done}/{b.total}</span>
                          <span style={{ fontSize: 12, fontWeight: 800, color: b.pct === 100 ? "#4bce97" : "#5a7a9a", fontFamily: "'DM Mono', monospace" }}>{b.pct}%</span>
                        </div>
                      </div>
                      <div style={{ width: "100%", height: 5, borderRadius: 99, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${b.pct}%`, background: b.pct === 100 ? "#4bce97" : (b.color || "#579dff"), borderRadius: 99, transition: "width 1.2s cubic-bezier(.4,0,.2,1)", boxShadow: b.pct === 100 ? "0 0 8px #4bce9766" : `0 0 5px ${b.color||"#579dff"}44` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* Recent Cards */}
            <Panel>
              <SectionHeader title="Recent Cards" sub={`Latest ${recentCards.length}`} />
              {recentCards.length === 0 ? <Empty icon="🃏" msg="No cards yet" /> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {recentCards.map(card => {
                    const st = STATUS_CONFIG[card.status] || STATUS_CONFIG.todo;
                    const board = boards.find(b => b.id === card.colId);
                    const cl = card.checklist || [];
                    return (
                      <div key={card.id} className="drow" style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 10px", borderRadius: 10, transition: "background 0.15s", cursor: "default" }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: st.color, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: "#dce8f5", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{card.title}</p>
                          <p style={{ fontSize: 11, color: "#2d4a6a", margin: "2px 0 0" }}>
                            {board?.title || "—"}{cl.length > 0 ? ` · ☑ ${cl.filter(i=>i.done).length}/${cl.length}` : ""}
                          </p>
                        </div>
                        <Pill label={st.label} color={st.color} bg={st.bg} />
                        {card.createdAt && <span style={{ fontSize: 10, color: "#1a3050", whiteSpace: "nowrap" }}>{fmtTime(card.createdAt)}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </div>

          {/* Right */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Recent Messages */}
            <Panel>
              <SectionHeader title="Recent Messages" sub={`${unreadMessages} unread`} />
              {recentMessages.length === 0 ? <Empty icon="💬" msg="No messages yet" /> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {recentMessages.map((msg, i) => {
                    const isUser = msg.senderType !== "admin";
                    const content = msg.content?.startsWith("[IMAGE]") ? "📷 Photo" : msg.content?.replace(/^↩ ".*?" — /, "");
                    return (
                      <div key={msg.messageId ?? i} className="dmrow" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 8px", borderRadius: 10, transition: "background 0.15s", cursor: "default" }}>
                        <Avatar name={msg.user} size={30} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#dce8f5" }}>{msg.user}</span>
                            <span style={{ fontSize: 10, color: "#1a3050" }}>{fmtTime(msg.timestamp)}</span>
                          </div>
                          <p style={{ fontSize: 11, color: "#2d4a6a", margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {isUser ? "" : "You: "}{content}
                          </p>
                        </div>
                        {isUser && !msg.read && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#579dff", flexShrink: 0 }} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>

            {/* Admin Activity — superadmin only */}
            {isSuperAdmin && (
              <Panel>
                <SectionHeader title="Admin Activity" sub="Boards per admin" />
                {topAssignees.length === 0 ? <Empty icon="👥" msg="No assignees" /> : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {topAssignees.map(([username, count], i) => {
                      const admin = admins.find(a => a.username === username);
                      const maxCount = topAssignees[0][1];
                      return (
                        <div key={username} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 10, color: "#1a3050", fontFamily: "'DM Mono', monospace", width: 14, textAlign: "center" }}>#{i+1}</span>
                          <Avatar name={admin?.fullNames || username} size={28} picBase64={admin?.profilePicture} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#dce8f5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110 }}>{admin?.fullNames || username}</span>
                              <span style={{ fontSize: 11, color: "#579dff", fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{count}</span>
                            </div>
                            <MiniBar pct={(count / maxCount) * 100} color="#579dff" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Panel>
            )}

            {/* Quick Stats */}
            <Panel style={{ padding: "16px 18px" }}>
              <SectionHeader title="Quick Stats" />
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {[
                  { label: "Cards with checklist",   value: displayCards.filter(c => c.checklist?.length > 0).length,                                    icon: "☑️" },
                  { label: "Completed checklists",   value: displayCards.filter(c => c.checklist?.length > 0 && c.checklist.every(i => i.done)).length, icon: "✔️" },
                  { label: "Cards with description", value: displayCards.filter(c => c.description?.trim()).length,                                       icon: "📝" },
                  { label: "Active conversations",   value: convos.length,                                                                                icon: "💬" },
                  { label: "My active tasks",        value: assignedNotDone.length,                                                                       icon: "📌" },
                  ...(isSuperAdmin ? [{ label: "Approved admins", value: admins.filter(a => a.isApproved !== false).length, icon: "✅" }] : []),
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <span style={{ fontSize: 11.5, color: "#3a5a7a", display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12 }}>{s.icon}</span>{s.label}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#dce8f5", fontFamily: "'DM Mono', monospace" }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>

        {/* ── Superadmin: All Admins Table ── */}
        {isSuperAdmin && admins.length > 0 && (
          <Panel>
            <SectionHeader title="All Admins" sub={`${admins.length} total`} />
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Admin","Username","Role","Boards","Cards","Status"].map(h => (
                      <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:10, color:"#1e3a5a", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin, i) => {
                    const bCount = boards.filter(b => b.assignedTo === admin.username || b.createdBy === admin.username).length;
                    const cCount = cards.filter(c => !c.archived && (
                      c.createdBy === admin.username ||
                      boards.find(b => b.id === c.colId && (b.assignedTo === admin.username || b.createdBy === admin.username))
                    )).length;
                    const isApproved = admin.isApproved !== false;
                    return (
                      <tr key={admin.username || i} className="drow" style={{ transition:"background 0.15s" }}>
                        <td style={{ padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                            <Avatar name={admin.fullNames || admin.username} size={28} picBase64={admin.profilePicture} />
                            <span style={{ fontSize:13, fontWeight:600, color:"#dce8f5" }}>{admin.fullNames || admin.username}</span>
                          </div>
                        </td>
                        <td style={{ padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.03)", fontSize:12, color:"#2d4a6a", fontFamily:"'DM Mono', monospace" }}>@{admin.username}</td>
                        <td style={{ padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                          <Pill label={admin.role || "admin"} color={admin.role==="superadmin"?"#f59e0b":"#579dff"} bg={admin.role==="superadmin"?"rgba(245,158,11,0.1)":"rgba(87,157,255,0.1)"} />
                        </td>
                        <td style={{ padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.03)", fontSize:13, fontWeight:700, color:"#dce8f5", fontFamily:"'DM Mono', monospace" }}>{bCount}</td>
                        <td style={{ padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.03)", fontSize:13, fontWeight:700, color:"#dce8f5", fontFamily:"'DM Mono', monospace" }}>{cCount}</td>
                        <td style={{ padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.03)" }}>
                          <Pill label={isApproved?"Approved":"Pending"} color={isApproved?"#4bce97":"#f87168"} bg={isApproved?"rgba(75,206,151,0.1)":"rgba(248,113,104,0.1)"} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

      </div>
    </>
  );
}